import { useState, useEffect, useCallback, useMemo } from 'react';
import { useMutation, useQuery, useLazyQuery } from '@apollo/client';
import { ApplicantData, ScoreBreakdownItem } from '@trnsact/trnsact-scorecard-sdk';
import { FACT_NAMES_BOOLEAN, FACT_NAMES_NUMERIC, FACT_NAMES_STRING } from '@trnsact/business-criteria';
import _ from 'lodash';
import { SelectOption } from 'global';
import { ScorecardCriteria } from '../types';
import { CREATE_CREDIT_TIERS_RESULTS, GET_SCORECARD, RUN_SCORECARD } from '../queries';
import { LenderWaterfallPrescreenCriteriaResultType, CreditApplication } from '@trnsact/trnsact-shared-types';
import { ConditionsFieldsTypes } from '../../../../modules/jsonRulesCriterias/scorecardCriteria/types';
import { determineRuleType } from '../../../../modules/jsonRulesCriterias/scorecardCriteria/lib';
import { buildApplicantData, extractStringOptions } from '../utils/scorecardUtils';

export const useScorecard = (
  creditApplication: CreditApplication,
  equipmentData: any,
  vo: any,
  cs: any,
  account: any,
  portalConfigurationDataLoading: boolean,
  refetchVO?: any
) => {
  const [createCreditTiersResults, { error }] = useMutation(CREATE_CREDIT_TIERS_RESULTS);
  const [runScorecardQuery, { loading: runScorecardLoading }] = useLazyQuery(RUN_SCORECARD);
  const [scorecardCriteria, setScorecardCriteria] = useState<ScorecardCriteria | null>(null);
  const [financeProgramId, setFinanceProgramId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [totalScore, setTotalScore] = useState<number>(0);
  const [band, setBand] = useState<string>('');
  const [bandColor, setBandColor] = useState<string>('#FFF');
  const [breakDown, setBreakDown] = useState<ScoreBreakdownItem[]>([]);
  const [applicantData, setApplicantData] = useState<ApplicantData | null>(null);
  const [listOfFacts, setListOfFacts] = useState<
    Map<
      string,
      {
        fact: string;
        type: ConditionsFieldsTypes;
        factType: FACT_NAMES_BOOLEAN | FACT_NAMES_NUMERIC | FACT_NAMES_STRING;
      }
    >
  >(new Map());
  const [stringFactOptions, setStringFactOptions] = useState<Map<string, SelectOption[]>>(new Map());
  const [manualFields, setManualFields] = useState<{ [key: string]: any }>({});
  const [hasSavedResults, setHasSavedResults] = useState<boolean>(false);
  const [initialSavedResultsChecked, setInitialSavedResultsChecked] = useState<boolean>(false);
  const [savedResultsRestored, setSavedResultsRestored] = useState<boolean>(false);

  // Check for saved results IMMEDIATELY when vo is available, before building applicantData
  useEffect(() => {
    if (vo && !initialSavedResultsChecked) {
      if (vo.scoreCardResult?.runtimeOutput) {
        setHasSavedResults(true);
      }
      setInitialSavedResultsChecked(true);
    }
  }, [vo, initialSavedResultsChecked]);

  useQuery(GET_SCORECARD, {
    notifyOnNetworkStatusChange: true,
    onCompleted(data) {
      setScorecardCriteria(data.scoreCard[0]?.prescreenCriteria);
      setFinanceProgramId(data.scoreCard[0]?.financeProgramId);
      setIsLoading(false);
    },
  });

  useEffect(() => {
    setIsLoading(!creditApplication || !vo || runScorecardLoading);
  }, [creditApplication, vo, portalConfigurationDataLoading, runScorecardLoading]);

  // Load and restore saved scorecard results when applicantData is ready (only once)
  useEffect(() => {
    if (vo?.scoreCardResult && hasSavedResults && applicantData && !savedResultsRestored) {
      const savedResult = vo.scoreCardResult;
      if (savedResult.runtimeOutput) {
        const output = savedResult.runtimeOutput;
        setTotalScore(output.totalScore || 0);
        setBand(output.band || savedResult.highestTierPassed || '');
        setBandColor(output.bandColor || savedResult.highestTierColor || '#FFF');
        setBreakDown(output.breakdown || []);

        // Restore manual field overrides from saved runtimeInput
        if (savedResult.runtimeInput) {
          const savedInput = savedResult.runtimeInput;
          const manualOverrides: { [key: string]: any } = {};

          // FICO and PERSONAL_CREDIT_SCORE are always derived from live CBR data in buildApplicantData.
          // Excluding them here prevents stale saved values from overriding the live CBR-derived score.
          const CBR_DERIVED_FIELDS = new Set(['FICO', 'PERSONAL_CREDIT_SCORE']);

          // Extract fields that differ from the default applicantData
          Object.keys(savedInput).forEach(key => {
            if (!CBR_DERIVED_FIELDS.has(key) && applicantData[key] !== savedInput[key]) {
              manualOverrides[key] = savedInput[key];
            }
          });

          if (Object.keys(manualOverrides).length > 0) {
            setManualFields(manualOverrides);
          }
        }

        setSavedResultsRestored(true);
      }
    }
  }, [vo, hasSavedResults, applicantData, savedResultsRestored]);

  useEffect(() => {
    if (!isLoading && scorecardCriteria) {
      let applicant = buildApplicantData(creditApplication, equipmentData, vo, cs);

      // Convert manualFields values to their correct types
      const parsedManualFields: { [key: string]: any } = {};
      Object.entries(manualFields).forEach(([k, v]) => {
        if (typeof v === 'string') {
          if (v.trim() !== '' && !isNaN(Number(v))) {
            parsedManualFields[k] = Number(v);
          } else if (v === 'true' || v === 'false') {
            parsedManualFields[k] = v === 'true';
          } else {
            parsedManualFields[k] = v;
          }
        } else {
          parsedManualFields[k] = v;
        }
      });

      applicant = { ...applicant, ...parsedManualFields };

      const factsMap = new Map();

      scorecardCriteria.jsonCriteria.scoreCriteria.forEach((scoreCardRow: any) => {
        scoreCardRow?.jsonRules.conditions.all.forEach((cond: any) => {
          const factName = cond.fact;
          const fieldType = determineRuleType(factName);
          factsMap.set(factName, { fact: factName, type: fieldType, factType: factName });
        });

        if (scoreCardRow && scoreCardRow?.customCriteria) {
          Object.values(scoreCardRow?.customCriteria).forEach((criteria: any) => {
            let fieldType: ConditionsFieldsTypes;
            switch (criteria.type) {
              case 'boolean':
                fieldType = ConditionsFieldsTypes.Boolean;
                break;
              case 'number':
              case 'currency':
                fieldType = ConditionsFieldsTypes.Number;
                break;
              default:
                fieldType = ConditionsFieldsTypes.String;
            }
            factsMap.set(criteria.name, { fact: criteria.name, type: fieldType, factType: criteria.name });
          });
        }
      });

      setListOfFacts(factsMap);
      setStringFactOptions(extractStringOptions(scorecardCriteria));
      setApplicantData(applicant);
    }
  }, [isLoading, scorecardCriteria, manualFields, cs, creditApplication, equipmentData, vo]);

  const handleReRunScoreCard = useCallback(async () => {
    if (!scorecardCriteria || !applicantData || !financeProgramId) return;

    try {
      // Call the API to run the scorecard
      const { data } = await runScorecardQuery({
        variables: {
          input: {
            prescreenCriteriaId: scorecardCriteria.prescreenCriteriaId,
            applicantData,
            manualFields,
            vendorOpportunityId: vo?.vendorOpportunityId,
          },
        },
      });

      if (data?.runScorecard) {
        setTotalScore(data.runScorecard.totalScore || 0);
        setBandColor(data.runScorecard.bandColor || '#FFF');
        setBand(data.runScorecard.band || '');
        setBreakDown(data.runScorecard.breakdown || []);
      }
    } catch (error) {
      console.error('Error running scorecard:', error);
    }
  }, [scorecardCriteria, applicantData, manualFields, financeProgramId, vo, runScorecardQuery]);

  const debouncedReRunScoreCard = useMemo(() => _.debounce(handleReRunScoreCard, 800), [handleReRunScoreCard]);

  const handleSaveScoreCard = async (refetchCreditSubmission?: any) => {
    if (totalScore !== undefined && band && applicantData) {
      const results = {
        totalScore,
        band,
        bandColor,
        breakdown: breakDown,
      };

      const input = [
        {
          accountId: account?.id,
          vendorOpportunityId: vo?.vendorOpportunityId,
          processInstanceId: vo?.processInstanceId,
          prescreenCriteriaId: scorecardCriteria?.prescreenCriteriaId,
          creditSubmissionId: cs?.creditSubmissionId,
          financeProgramId,
          tiersType: 'SCORECARD',
          runtimeInput: applicantData,
          runtimeOutput: results,
          result: LenderWaterfallPrescreenCriteriaResultType.PASS,
          highestTierPassed: band || '',
          highestTierColor: bandColor || '',
        },
      ];
      setIsLoading(true);
      await handleCreateResults(input);
      if (refetchCreditSubmission) {
        await refetchCreditSubmission();
      }
      if (refetchVO) {
        //no need to wait
        refetchVO();
      }
      setIsLoading(false);
    }
  };

  const handleCreateResults = async (inputArray: any) => {
    try {
      await createCreditTiersResults({ variables: { input: inputArray } });
    } catch (err) {
      console.log(error);
    }
  };

  useEffect(() => {
    // Only skip initial auto-run if saved results exist AND user hasn't changed any fields
    const hasManualChanges = Object.keys(manualFields).length > 0;
    const shouldRun = applicantData && financeProgramId && (!hasSavedResults || hasManualChanges);

    if (shouldRun) {
      debouncedReRunScoreCard();
    }
    return () => {
      debouncedReRunScoreCard.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [financeProgramId, manualFields]);

  return {
    scorecardCriteria,
    isLoading,
    totalScore,
    band,
    bandColor,
    breakDown,
    applicantData,
    listOfFacts,
    stringFactOptions,
    manualFields,
    setManualFields,
    handleSaveScoreCard,
    handleReRunScoreCard,
  };
};
